// vim: set ts=2 sts=2 sw=2 et:
//
// Respaldo manual del torneo en la web: "Respaldar en la web" (header) sube el estado completo de esta
// PC, el mismo contenido que "Guardar como"; "Recuperar de la web" (Inicio) lo baja en otra PC. La web
// guarda una copia por PC, así que ninguna pisa la copia de otra.

import type { GlobalState } from "../../types/stateTypes";
import { stateVersion } from "../../versions";
import { bundleHasMeet } from "./buildExportBundle";

export type MeetSummary = { athletes: number; judgedAttempts: number };

export function summarizeMeet(state: Pick<GlobalState, "registration">): MeetSummary {
  let judgedAttempts = 0;
  for (const entry of state.registration.entries) {
    for (const statuses of [entry.squatStatus, entry.benchStatus, entry.deadliftStatus]) {
      for (const status of statuses) if (status !== 0) judgedAttempts++;
    }
  }
  return { athletes: state.registration.entries.length, judgedAttempts };
}

const plural = (count: number, one: string, many: string): string => `${count} ${count === 1 ? one : many}`;

/** "210 atletas, 640 intentos juzgados". */
export function describeSummary(summary: MeetSummary): string {
  return `${plural(summary.athletes, "atleta", "atletas")}, ${plural(summary.judgedAttempts, "intento juzgado", "intentos juzgados")}`;
}

/** Por qué no se puede respaldar, o null si se puede. Vacío o sin vínculo con la web, no se sube nada. */
export function webBackupBlocker(state: GlobalState): string | null {
  const session = state.wrpfSession;
  if (!session.tournamentId || !session.callbackToken || !session.callbackUrl) {
    return "Abrí el torneo desde la web para poder respaldarlo.";
  }
  if (!bundleHasMeet(state) || state.registration.entries.length === 0) {
    return "No hay torneo cargado para respaldar.";
  }
  return null;
}

/** Endpoint de sync: el que mandó la web o, si no, el hermano de /results. */
export function syncUrlFrom(session: GlobalState["wrpfSession"]): string | null {
  if (session.syncUrl) return session.syncUrl;
  if (!session.callbackUrl) return null;
  try {
    return new URL("sync", session.callbackUrl).toString();
  } catch {
    return session.callbackUrl.replace(/\/results\/?$/, "/sync");
  }
}

/** Pregunta antes de reemplazar una copia de la web que tiene más intentos juzgados que esta PC. */
export function overwriteWarning(local: MeetSummary, remote: MeetSummary | null): string | null {
  if (remote === null || remote.judgedAttempts <= local.judgedAttempts) return null;
  return (
    `La copia de esta PC en la web tiene ${remote.judgedAttempts} intentos juzgados y esta PC tiene ` +
    `${local.judgedAttempts}. ¿Reemplazarla igual?`
  );
}

type MaybeBackup = Partial<GlobalState> & { _wrpf?: { tournamentId?: unknown } };

/** Valida que la copia bajada sea un torneo de OpenLifter del MISMO torneo y versión de formato. */
export function backupFromWebError(payload: unknown, current: GlobalState): string | null {
  const copy = payload as MaybeBackup | null;
  if (!copy || typeof copy !== "object" || !copy.meet || !copy.registration || !copy.lifting || !copy.versions) {
    return "La copia de la web no es un torneo de OpenLifter.";
  }
  if (copy.versions.stateVersion !== stateVersion) {
    return `La copia se guardó con otra versión de OpenLifter (${copy.versions.releaseVersion}).`;
  }
  const tournamentId = copy.wrpfSession?.tournamentId ?? copy._wrpf?.tournamentId;
  if (tournamentId !== current.wrpfSession.tournamentId) {
    return "La copia es de otro torneo.";
  }
  return null;
}

/**
 * Estado a cargar desde la copia. Conserva el día, tarima, vuelo y movimiento que esta PC está
 * operando: la copia pudo guardarse mirando otro día y el operador no tiene por qué perder su lugar.
 * Conserva también los récords de esta PC: las copias viajan sin los récords vigentes (ver
 * buildWebUploadBundle) y la PC los tiene frescos de cuando abrió el torneo desde la web.
 */
export function stateFromWebBackup(copy: GlobalState, current: GlobalState): GlobalState {
  return { ...copy, lifting: current.lifting, wrpfRecords: current.wrpfRecords };
}
